import axios, { AxiosInstance, AxiosError } from 'axios'
import { authService } from './auth'

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api'

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = authService.getToken()
        console.log('API Request:', config.url, '- Token:', token ? token.substring(0, 20) + '...' : 'NONE')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const status = error.response?.status
        const url = error.config?.url
        console.log('API Error:', url, '- Status:', status)
        if (status === 401) {
          console.log('401 detected - logging out')
          authService.logout()
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  // Generic request methods
  async get<T>(url: string, params?: object): Promise<T> {
    const response = await this.client.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: object): Promise<T> {
    const response = await this.client.post(url, data)
    return response.data
  }

  async put<T>(url: string, data?: object): Promise<T> {
    const response = await this.client.put(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete(url)
    return response.data
  }

  // Specific API methods
  // Auth
  login(username: string, password: string) {
    return this.post('/auth?action=login', { username, password })
  }

  verifyToken() {
    return this.get('/auth?action=verify')
  }

  // Zones (from zone files)
  getZones(active?: boolean) {
    return this.get('/zones?action=list')
  }

  getZone(id: number) {
    return this.get(`/zones?action=get&id=${id}`)
  }

  createZone(data: object) {
    return this.post('/zones?action=create', data)
  }

  updateZone(id: number, data: object) {
    return this.put(`/zones?action=update&id=${id}`, data)
  }

  deleteZone(id: number) {
    return this.delete(`/zones?action=delete&id=${id}`)
  }

  deployZone(id: number) {
    return this.post(`/zones?action=deploy&id=${id}`)
  }

  previewZone(id: number) {
    return this.get(`/zones?action=get&id=${id}`)
  }

  getZoneStats() {
    return this.get('/zones?action=stats')
  }

  // Records
  getRecords(zoneId: number) {
    return this.get(`/records?zone_id=${zoneId}`)
  }

  createRecord(data: object) {
    return this.post('/records', data)
  }

  updateRecord(id: number, data: object) {
    return this.put(`/records?id=${id}`, data)
  }

  deleteRecord(id: number) {
    return this.delete(`/records?id=${id}`)
  }

  searchRecords(query: string, zoneId?: number) {
    return this.get('/records', { q: query, zone_id: zoneId })
  }

  // Monitoring
  getNetworkSummary() {
    return this.get('/monitoring?action=network-summary')
  }

  getInterfaces() {
    return this.get('/monitoring?action=interfaces')
  }

  getInterfaceStats(name: string) {
    return this.get('/monitoring?action=network', { interface: name })
  }

  getSystemInfo() {
    return this.get('/monitoring?action=system-info')
  }

  getInterfaceHistory(id: number, period?: string) {
    return this.get('/monitoring?action=interface-history', { id, period })
  }

  getDnsStatus() {
    return this.get('/monitoring?action=dns-status')
  }

  getDnsStats(hours?: number) {
    return this.get('/monitoring?action=dns-stats', { hours })
  }

  getDnsQueries(limit?: number) {
    return this.get('/monitoring?action=dns-queries', { limit })
  }

  getAlerts(resolved?: boolean, limit?: number) {
    return this.get('/monitoring?action=alerts', { resolved, limit })
  }

  // DNS Server
  getDNSServerStatus() {
    return this.get('/bind?action=status')
  }

  startDNSServer() {
    return this.post('/bind?action=start')
  }

  stopDNSServer() {
    return this.post('/bind?action=stop')
  }

  restartDNSServer() {
    return this.post('/bind?action=restart')
  }

  reloadDNSServer() {
    return this.post('/bind?action=reload')
  }

  getDNSServerConfig() {
    return this.get('/bind?action=config')
  }

  saveDNSServerConfig(content: string) {
    return this.put('/bind?action=config', { content })
  }

  validateBindConfig(content?: string) {
    return this.post('/bind?action=validate', content ? { content } : {})
  }

  getDNSServerLogs() {
    return this.get('/bind?action=logs')
  }

  flushCache() {
    return this.post('/bind?action=flush-cache')
  }

  // DNS Traffic
  getDnsTrafficStats() {
    return this.get('/dns-traffic?action=stats')
  }

  getLiveDnsTraffic() {
    return this.get('/dns-traffic?action=live')
  }

  getRecentQueries() {
    return this.get('/dns-traffic?action=queries')
  }

  getPtrRecords() {
    return this.get('/ptr')
  }

  updatePtrRecord(id: number, data: object) {
    return this.put(`/ptr?id=${id}`, data)
  }

  // Users
  getUsers() {
    return this.get('/users')
  }

  getUser(id: number) {
    return this.get(`/users?id=${id}`)
  }

  createUser(data: object) {
    return this.post('/users', data)
  }

  updateUser(id: number, data: object) {
    return this.put(`/users?id=${id}`, data)
  }

  deleteUser(id: number) {
    return this.delete(`/users?id=${id}`)
  }

  // Backups
  getBackups() {
    return this.get('/backups?action=list')
  }

  createBackup() {
    return this.post('/backups?action=create')
  }

  // Settings
  getSettings() {
    return this.get('/settings')
  }

  updateSettings(settings: object) {
    return this.put('/settings', { settings })
  }

  // Logout
  logout() {
    return this.post('/auth?action=logout')
  }
}

export const apiService = new ApiService()
export default apiService
